import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Icon } from '../../atoms/Icon/Icon';
import { Tooltip, type TooltipArrow } from '../../atoms/Tooltip/Tooltip';
import styles from './HelpIcon.module.css';

/**
 * Figma: `Help icon` (99:4839) — "Question-mark icon that reveals a tooltip on
 * interaction. Open: True | False — whether the tooltip is visible. Supporting
 * text: True | False. Tooltip: Top no arrow | Top arrow | Left | Top left |
 * Bottom | Right | Top right. Properties: Cursor (boolean — show/hide hover
 * cursor). 28 total variants."
 *
 * "Place next to form labels, settings, or unfamiliar terms to offer inline
 * help… Use alongside the Required * indicator and labels in form components
 * like Select and Input fields."
 *
 * The trigger is no longer a question mark. It became `info` (412:46683), the
 * circled "i", and the 2026-09 update moved it again: the `Icon` instance-swap
 * default is now `globe` (518:14343). That same update grew the trigger from
 * 12x12 to 16x16; **the 2026-09-16 update put it back to 12x12** across all 28
 * variants. The annotation still says "Question-mark icon" and still lists only
 * `Cursor` under properties — stale on both counts — but its "Size: 12 × 12px"
 * is accurate again. See plan.md §7.
 *
 * `Open` is real state — hover, focus or a tap — so it is not a variant prop;
 * pass `open` only to drive it from outside. `Cursor` is the mouse-pointer
 * graphic Figma uses to depict hover in a static file, not a UI element, so it
 * has no counterpart here.
 */
export type HelpIconPlacement =
  | 'topNoArrow'
  | 'topArrow'
  | 'topLeft'
  | 'topRight'
  | 'bottom'
  | 'left'
  | 'right';

/** Which way the bubble's arrow points for each placement. */
const placementArrow: Record<HelpIconPlacement, TooltipArrow> = {
  topNoArrow: 'none',
  topArrow: 'bottomCenter',
  topLeft: 'bottomRight',
  topRight: 'bottomLeft',
  bottom: 'topCenter',
  left: 'right',
  right: 'left',
};

const placementClass: Record<HelpIconPlacement, string> = {
  topNoArrow: styles.top,
  topArrow: styles.top,
  topLeft: styles.topLeft,
  topRight: styles.topRight,
  bottom: styles.bottom,
  left: styles.left,
  right: styles.right,
};

/**
 * Gap kept between a bubble and the viewport edge. Not in Figma — the file
 * draws every bubble with room to spare — so it borrows the 8 step.
 */
const VIEWPORT_GUTTER = 8;
/** Figma's trigger-to-bubble gap, `--space-4`, mirrored for the fit maths. */
const GAP = 4;
/** Where the `Top right` / `Top left` arrow lands, `--space-20` in from the edge. */
const OFFSET_ARROW_INSET = 20;

/** The placement to fall back to when the preferred one would leave the viewport. */
const opposite: Record<HelpIconPlacement, HelpIconPlacement> = {
  topNoArrow: 'bottom',
  topArrow: 'bottom',
  topLeft: 'bottom',
  topRight: 'bottom',
  bottom: 'topArrow',
  left: 'right',
  right: 'left',
};

type Fit = { placement: HelpIconPlacement; shift: number };

/**
 * Where the bubble would land for `placement`, from the trigger's box and the
 * bubble's untransformed size. Pure arithmetic mirroring the stylesheet, so the
 * decision never depends on which placement happens to be rendered.
 */
function bubbleBox(placement: HelpIconPlacement, a: DOMRect, w: number, h: number) {
  const cx = a.left + a.width / 2;
  const cy = a.top + a.height / 2;
  switch (placement) {
    case 'topNoArrow':
    case 'topArrow':
      return { x: cx - w / 2, y: a.top - GAP - h };
    case 'topRight':
      return { x: cx - OFFSET_ARROW_INSET, y: a.top - GAP - h };
    case 'topLeft':
      return { x: cx + OFFSET_ARROW_INSET - w, y: a.top - GAP - h };
    case 'bottom':
      return { x: cx - w / 2, y: a.bottom + GAP };
    case 'right':
      return { x: a.right + GAP, y: cy - h / 2 };
    case 'left':
      return { x: a.left - GAP - w, y: cy - h / 2 };
  }
}

/** Space the box would push past the viewport, summed over all four edges. */
function overflow(b: { x: number; y: number }, w: number, h: number, vw: number, vh: number) {
  const g = VIEWPORT_GUTTER;
  return (
    Math.max(0, g - b.x) +
    Math.max(0, g - b.y) +
    Math.max(0, b.x + w - (vw - g)) +
    Math.max(0, b.y + h - (vh - g))
  );
}

/**
 * Flip to the opposite side when the preferred one is cut off on its own axis
 * and the opposite is not worse, then slide along the cross axis until the
 * bubble is inside the viewport. The arrow slides back by the same amount so it
 * still points at the trigger.
 */
function fit(preferred: HelpIconPlacement, a: DOMRect, w: number, h: number): Fit {
  const vw = document.documentElement.clientWidth;
  const vh = document.documentElement.clientHeight;

  let placement = preferred;
  const here = overflow(bubbleBox(preferred, a, w, h), w, h, vw, vh);
  if (here > 0) {
    const alt = opposite[preferred];
    if (overflow(bubbleBox(alt, a, w, h), w, h, vw, vh) < here) placement = alt;
  }

  const box = bubbleBox(placement, a, w, h);
  const g = VIEWPORT_GUTTER;
  const vertical = placement === 'left' || placement === 'right';
  const [start, size, limit] = vertical ? [box.y, h, vh] : [box.x, w, vw];
  const shift = Math.min(Math.max(start, g), Math.max(g, limit - g - size)) - start;

  return { placement, shift: vertical ? 0 : shift };
}

export type HelpIconProps = {
  /** Figma's `Text` on the nested Tooltip — the explanation. */
  text: string;
  /** Figma `Supporting text`. Omit for the single-line bubble. */
  supportingText?: string;
  /** Figma `Tooltip`. */
  placement?: HelpIconPlacement;
  /** Figma `Open`. Omit to let the icon own it. */
  open?: boolean;
  /**
   * Figma `Icon` (instance swap) — the trigger glyph. Defaults to `globe`, which
   * is what the design ships as of 2026-09. Whatever is passed is drawn at 12x12
   * in `icon/secondary`, as Figma scales the swapped instance.
   */
  icon?: ReactNode;
  onOpenChange?: (open: boolean) => void;
  /**
   * Accessible name for the trigger. The icon is the button's only content, so
   * it needs one; the tooltip is the *description*, not the name. Not in Figma.
   */
  label?: string;
  className?: string;
};

export function HelpIcon({
  text,
  supportingText,
  placement = 'topNoArrow',
  open,
  icon,
  onOpenChange,
  label = 'More information',
  className,
}: HelpIconProps) {
  const tooltipId = useId();
  const rootRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isOpen = open ?? uncontrolledOpen;

  /*
   * The bubble outlives `isOpen` by its exit, the same way `Popover`'s panel
   * does, so it can fade out instead of vanishing. Adjusted during render
   * rather than in an effect so no frame paints it closed-but-unmounted.
   */
  const [exiting, setExiting] = useState(false);
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    setExiting(!isOpen);
  }

  /*
   * The exit ends on the bubble's opacity `transitionend` — except when there
   * is no exit to run. The appear waits `--tt-delay` before it moves, so a
   * pointer that crosses the icon inside those 80ms closes a bubble still at
   * opacity 0: 0 to 0 is no transition, no event arrives, and the bubble would
   * stay mounted, invisible, for good. One frame after closing, a bubble with
   * nothing running is done.
   */
  useEffect(() => {
    if (!exiting) return;
    const frame = requestAnimationFrame(() => {
      const bubble = anchorRef.current?.firstElementChild;
      if (!bubble || bubble.getAnimations().length === 0) setExiting(false);
    });
    return () => cancelAnimationFrame(frame);
  }, [exiting]);

  /*
   * An absolutely-positioned bubble is clipped by any ancestor that sets
   * `overflow`, and `Table` sets it twice: the card hides overflow to mask its
   * rounded corners, and the body scrolls horizontally, which forces the
   * vertical axis to clip too. Inside a table header the bubble was sliced in
   * half.
   *
   * The fix is an *anchor* that mirrors the trigger's box and is
   * `position: fixed`, because a fixed box is positioned against the viewport
   * and ancestor `overflow` does not clip it. The placement rules in the
   * stylesheet are then untouched — every `bottom: calc(100% + …)` and
   * `left: 50%` still resolves against the same 16x16 box.
   *
   * It stays in place rather than being portalled to `document.body`: a portal
   * escapes clipping too, but it also moves the bubble out of the trigger's
   * subtree, which breaks every ancestor-scoped selector — the parity gate for
   * this component addresses its tooltips as `#p-top-arrow div[class*=tooltip]`.
   *
   * The one thing that would defeat this is an ancestor with `transform`,
   * `filter`, `perspective` or `contain`, since any of those makes itself the
   * containing block for fixed descendants. Nothing between here and the table
   * card has one.
   */
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);

  /*
   * The file places every bubble with room to spare; a real page does not. A
   * card header at the top of the scroll area put its `Top right` bubble above
   * the viewport, and the title line was sliced off. So the placement is a
   * preference: `fit` flips it when that side is cut off and slides the bubble
   * back inside along the other axis.
   *
   * Measured with `offsetWidth`/`offsetHeight`, not `getBoundingClientRect`:
   * the bubble's entrance scales it from 0.98, and a mid-transition rect would
   * under-report its size on the very frame this runs.
   */
  const [resolved, setResolved] = useState<Fit>({ placement, shift: 0 });

  useLayoutEffect(() => {
    const bubble = anchorRef.current?.firstElementChild as HTMLElement | null;
    if (!isOpen || anchor === null || !bubble) return;
    const next = fit(placement, anchor, bubble.offsetWidth, bubble.offsetHeight);
    setResolved((prev) =>
      prev.placement === next.placement && prev.shift === next.shift ? prev : next,
    );
  }, [isOpen, anchor, placement]);

  useLayoutEffect(() => {
    if (!isOpen) return;

    const measure = () => {
      if (triggerRef.current) setAnchor(triggerRef.current.getBoundingClientRect());
    };
    measure();

    /* `capture` so a scroll on any ancestor — the table body included — moves
       the bubble with its trigger rather than leaving it behind. */
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [isOpen]);

  const setOpen = useCallback(
    (next: boolean) => {
      if (open === undefined) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [open, onOpenChange],
  );

  /*
   * Escape closes, and a pointer-down anywhere else does too — a tap-opened
   * tooltip on a touch device has no pointerleave to close it. Neither is in
   * Figma, which describes appearance only.
   */
  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Consumed so an open bubble inside a modal does not close the modal too (deepdive-v3).
      event.preventDefault();
      setOpen(false);
    };
    const onPointerDown = (event: PointerEvent) => {
      const root = rootRef.current;
      const target = event.target as Node | null;
      if (root && target && !root.contains(target)) setOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [isOpen, setOpen]);

  return (
    <span ref={rootRef} className={[styles.root, className].filter(Boolean).join(' ')}>
      <button
        ref={triggerRef}
        className={styles.trigger}
        type="button"
        aria-label={label}
        aria-describedby={isOpen ? tooltipId : undefined}
        aria-expanded={isOpen}
        onPointerEnter={() => setOpen(true)}
        onPointerLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen(!isOpen)}
      >
        <span className={styles.icon}>{icon ?? <Icon name="globe" />}</span>
      </button>

      {(isOpen || exiting) && anchor !== null && (
        <span
          ref={anchorRef}
          className={styles.anchor}
          /* On the way out it is still painted but no longer describes anything. */
          aria-hidden={isOpen ? undefined : true}
          onTransitionEnd={(event) => {
            if (
              !isOpen &&
              event.target === anchorRef.current?.firstElementChild &&
              event.propertyName === 'opacity'
            ) {
              setExiting(false);
            }
          }}
          style={
            {
              left: anchor.left,
              top: anchor.top,
              width: anchor.width,
              height: anchor.height,
              '--help-shift': `${resolved.shift}px`,
            } as CSSProperties
          }
        >
          <Tooltip
            id={tooltipId}
            className={`${styles.tooltip} ${placementClass[resolved.placement]}`}
            arrow={placementArrow[resolved.placement]}
            supportingText={supportingText}
            open={isOpen}
          >
            {text}
          </Tooltip>
        </span>
      )}
    </span>
  );
}
